import { InfiniteData, QueryKey } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { queryClient } from '@/src/lib/react-query/react-query';
import { RootState } from '@/src/reducers/types';
import { MyPortfolioTasksResponse } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/api/getMyPortfolioTasks';
import {
  SocketData,
  useMyPortfolio,
} from '@/src/components/myTasks2/components/Tabs/MyPortfolio/store/myPortfolio.store';
import {
  invalidateMyPortfolioCalendar,
  invalidateMyPortfolioCatchup,
  invalidateMyTasksCalendar,
  invalidateMyTasksCatchup,
  invalidateMyTasksDashboard,
} from './helper';
import { getMyTasks } from './components/Tabs/MyTasks/api/getMyTasks';

const getTaskIdsFromQueryData = (data: SocketData, queryKey: QueryKey) => {
  const foundTaskIds = [];
  queryClient
    .getQueriesData([queryKey])
    .forEach((queryData: [QueryKey, InfiniteData<MyPortfolioTasksResponse>]) => {
      if (!queryData[1]) {
        return false;
      }
      return data.taskIds.forEach((taskId) =>
        queryData[1].pages.forEach((page) =>
          page.tasks.forEach((task) => {
            if (task.taskId === +taskId) {
              foundTaskIds.push(+taskId);
            }
          })
        )
      );
    });
  return foundTaskIds;
};

const deleteTaskFromQuerys = (data: SocketData, queryKey: QueryKey) => {
  return queryClient.setQueriesData([queryKey], (prev: any) => {
    if (!prev) return prev;
    return {
      ...prev,
      pages: prev.pages.map((page) => {
        return {
          ...page,
          tasks: page.tasks.filter((task) => !data.taskIds.includes(task.taskId)),
          count: page.count - data.taskIds.length,
        };
      }),
    };
  });
};

const updateQueryDataForTaskIds = async (taskIds: number[], queryKey: QueryKey) => {
  const { tasks } = await getMyTasks({
    taskIds,
  });
  const { tasks: completedTasks } = await getMyTasks({
    taskIds,
  });
  const allTasks = [...tasks, ...completedTasks];
  queryClient.setQueriesData([queryKey], (queryData: InfiniteData<MyPortfolioTasksResponse>) => {
    if (!queryData) return queryData;
    const updatedList = queryData.pages.map((pageData) => ({
      ...pageData,
      tasks: pageData.tasks.map((pageDataTask) => {
        const foundTask = allTasks.find((task) => pageDataTask.taskId === task.taskId);
        if (foundTask) {
          return foundTask;
        }
        return pageDataTask;
      }),
    }));
    return {
      ...queryData,
      pages: updatedList,
    };
  });
};

interface UseTaskSubscriptionsOptions {
  queryKey: QueryKey;
  selectedWeek?: string;
  origin?:
    | 'myTasksCalendar'
    | 'myTasksCatchup'
    | 'myPortfolioCalendar'
    | 'myPortfolioCatchup'
    | 'myTasksDashboard';
}

const TASK_UPDATED_SOCKET_CHANNEL_ID = 'TASK_UPDATED';
const TASK_DELETED_SOCKET_CHANNEL_ID = 'TASK_DELETED';
const TASK_CREATED_SOCKET_CHANNEL_ID = 'TASK_CREATED';

const invalidateByOrigin = (
  origin: string | undefined,
  selectedWeek?: string,
  queryKey?: QueryKey
) => {
  console.log('invalidateByOrigin called with:', origin, queryKey);
  switch (origin) {
    case 'myTasksCalendar':
      invalidateMyTasksCalendar(selectedWeek);
      break;
    case 'myTasksCatchup':
      invalidateMyTasksCatchup();
      break;
    case 'myPortfolioCalendar':
      invalidateMyPortfolioCalendar(selectedWeek);
      break;
    case 'myPortfolioCatchup':
      invalidateMyPortfolioCatchup();
      break;
    case 'myTasksDashboard':
      // Invalidate only the exact query key for the current tab
      if (queryKey) {
        invalidateMyTasksDashboard(queryKey);
      } else {
        invalidateMyTasksDashboard();
      }
      break;
    default:
      break;
  }
};

const useMyTasksSubscriptions = ({
  queryKey,
  selectedWeek,
  origin,
}: UseTaskSubscriptionsOptions) => {
  const socket = useSelector((store: RootState) => store.socket.container);
  const loggedUserId = useSelector((store: RootState) => store.loggedUser?.data?.id);
  const { socketData, setSocketData, resetSocketData } = useMyPortfolio();
  const [updatedTaskId, setUpdatedTaskId] = useState<number | null>(null);

  useEffect(() => {
    if (!socket) {
      return;
    }
    const handleCreated = (data: SocketData) => {
      console.log(TASK_CREATED_SOCKET_CHANNEL_ID, { data });
      if (loggedUserId === data.userId) {
        resetSocketData();
        invalidateByOrigin(origin, selectedWeek, queryKey);
        return;
      }
      setSocketData({
        ...socketData,
        created: [...(socketData?.created || []), ...data.taskIds.map(Number)],
      });
    };
    const handleUpdated = (data: SocketData) => {
      console.log(TASK_UPDATED_SOCKET_CHANNEL_ID, { data });
      if (!data) {
        return;
      }
      if (data.taskIds && data.taskIds.length > 0 && selectedWeek !== undefined) {
        setUpdatedTaskId(data.taskIds[0]);
        invalidateByOrigin(origin, selectedWeek, queryKey);
        console.log('Updated taskIds:', data.taskIds);
      }
      if (selectedWeek === undefined && origin === 'myTasksDashboard') {
        invalidateByOrigin(origin, undefined, queryKey);
      }
      const foundTaskIds = getTaskIdsFromQueryData(data, queryKey);
      console.log('Found taskIds in query data:', foundTaskIds);
      if (foundTaskIds.length === 0) return;
      if (loggedUserId === data.userId) {
        updateQueryDataForTaskIds(foundTaskIds, queryKey);
        return;
      }
      setSocketData({
        ...socketData,
        updated: [...(socketData?.updated || []), ...data.taskIds.map(Number)],
      });
    };
    const handleDeleted = (data: SocketData) => {
      console.log(TASK_DELETED_SOCKET_CHANNEL_ID, { data });
      if (!data) {
        return;
      }
      invalidateByOrigin(origin, selectedWeek, queryKey);
      const isTaskFound = getTaskIdsFromQueryData(data, queryKey).length > 0;
      if (!isTaskFound) return;
      if (loggedUserId === data.userId) {
        deleteTaskFromQuerys(data, queryKey);
        return;
      }
      setSocketData({
        ...socketData,
        deleted: [...(socketData?.deleted || []), ...data.taskIds.map(Number)],
      });
    };
    socket.on(TASK_CREATED_SOCKET_CHANNEL_ID, handleCreated);
    socket.on(TASK_UPDATED_SOCKET_CHANNEL_ID, handleUpdated);
    socket.on(TASK_DELETED_SOCKET_CHANNEL_ID, handleDeleted);
    return () => {
      socket.off(TASK_CREATED_SOCKET_CHANNEL_ID, handleCreated);
      socket.off(TASK_UPDATED_SOCKET_CHANNEL_ID, handleUpdated);
      socket.off(TASK_DELETED_SOCKET_CHANNEL_ID, handleDeleted);
    };
  }, [
    socket,
    loggedUserId,
    socketData,
    setSocketData,
    resetSocketData,
    selectedWeek,
    queryKey,
    origin,
  ]);
  return { updatedTaskId };
};

export default useMyTasksSubscriptions;
